import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Cookie, X, Settings } from 'lucide-react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Immer aktiv
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    const isLandingPage = window.location.pathname === '/' || window.location.pathname === '/landing';
    
    if (!consent) {
      // Show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    } else if (isLandingPage) {
      // On landing page, always show banner for first-time visitors
      // But if they've been here in the last 30 days, don't show again
      const lastShown = localStorage.getItem('cookie-banner-last-shown');
      if (!lastShown || Date.now() - parseInt(lastShown) > 30 * 24 * 60 * 60 * 1000) {
        setTimeout(() => setShowBanner(true), 1000);
      }
      
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Error loading cookie preferences', e);
      }
    } else {
      // Load saved preferences without showing banner
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Error loading cookie preferences', e);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie-banner-last-shown', Date.now().toString());
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookie-consent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookie-banner-last-shown', Date.now().toString());
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('cookie-banner-last-shown', Date.now().toString());
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex items-end justify-center pointer-events-none">
      {/* Banner */}
      <Card className="relative w-full max-w-6xl m-4 mb-8 bg-[#0a0a0a] border-gray-800 shadow-2xl pointer-events-auto">
        <CardContent className="p-6">
          {!showSettings ? (
            // Main Cookie Banner
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
              {/* Icon & Text */}
              <div className="flex-1">
                <div className="flex items-start gap-4">
                  <Cookie className="h-8 w-8 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      Diese Website verwendet Cookies
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Wir verwenden Cookies, um Ihnen das beste Erlebnis auf unserer Website zu bieten. 
                      Einige Cookies sind technisch notwendig, andere helfen uns, die Website zu verbessern. 
                      Sie können Ihre Einstellungen jederzeit anpassen.
                    </p>
                    <a 
                      href="/datenschutz" 
                      className="text-blue-400 hover:text-blue-300 text-sm mt-2 inline-block"
                    >
                      Mehr Informationen in unserer Datenschutzerklärung
                    </a>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3 w-full md:w-auto">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-blue-600 hover:bg-blue-700 w-full md:w-48"
                >
                  Alle akzeptieren
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="border-gray-700 w-full md:w-48"
                >
                  Nur Notwendige
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="ghost"
                  className="text-gray-400 hover:text-white w-full md:w-48"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Einstellungen
                </Button>
              </div>
            </div>
          ) : (
            // Cookie Settings
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Cookie-Einstellungen</h3>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Notwendige Cookies</h4>
                      <p className="text-sm text-gray-400">
                        Diese Cookies sind für den Betrieb der Website erforderlich und können nicht 
                        deaktiviert werden (z.B. Session-Cookies, Sicherheits-Cookies).
                      </p>
                    </div>
                    <div className="ml-4">
                      <div className="text-green-500 font-semibold">Immer aktiv</div>
                    </div>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Funktionale Cookies</h4>
                      <p className="text-sm text-gray-400">
                        Diese Cookies ermöglichen erweiterte Funktionalitäten wie z.B. das Speichern 
                        Ihrer Einstellungen und Präferenzen.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences({...preferences, functional: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer 
                                    peer-checked:after:translate-x-full peer-checked:after:border-white 
                                    after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                    after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all 
                                    peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Analyse-Cookies</h4>
                      <p className="text-sm text-gray-400">
                        Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                        indem Informationen anonym gesammelt und gemeldet werden.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer 
                                    peer-checked:after:translate-x-full peer-checked:after:border-white 
                                    after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                    after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all 
                                    peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Marketing-Cookies</h4>
                      <p className="text-sm text-gray-400">
                        Diese Cookies werden verwendet, um Besuchern auf Webseiten zu folgen und 
                        relevante Werbung anzuzeigen.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer 
                                    peer-checked:after:translate-x-full peer-checked:after:border-white 
                                    after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                    after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all 
                                    peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Save Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Auswahl speichern
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="flex-1 border-gray-700"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CookieBanner;
